import React, { useState, useEffect } from 'react';
import { ChevronLeft, Plus, X, Briefcase, FileText, Anchor, Gift, ChevronDown, ChevronUp, MapPin, DollarSign, Users, Award, Calendar, Camera } from 'lucide-react';
import './CreateJob.css';
import Dashboard from './Dashboard.js';
import { useNavigate } from 'react-router';
import RequirementList from '../components/RequirementList'
import ImageUrlUploader from '../components/ImageUrlUploader'
import JobReview from '../components/JobReview'
import FormSubmissionStatus from '../components/FormSubmissionStatus'
import { useCompany } from '../context/CompanyContext';

const JobPostingForm = () => {
  const { selectedCompany } = useCompany();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [cities, setCities] = useState([]);
  const [skillsCategories, setSkillsCategories] = useState({});
  const [jobCategories, setJobCategories] = useState({});
  const [filteredCities, setFilteredCities] = useState([]);
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [citySearchLoading, setCitySearchLoading] = useState(false);
  const [isLoadingSkills, setIsLoadingSkills] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedJobCategories, setExpandedJobCategories] = useState({});
  const [isSubmitting, setSubmitting] = useState(false);
  const [hideForm, setHideForm] = useState(false);
  const [submissionResult, setSubmissionResult] = useState({
    'success': false,
    'jobId': '',
    'error': true,
    'message': '',
    'errors': []
  });

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    job_type: '',
    salary_lower_rate: '',
    salary_higher_rate: '',
    currency_code: 'USD',
    category: '',
    skills: [],
    description: '',
    benefits: [{
        id: Date.now(),
        benefit_name: '',
        benefit_description: ''
      }],
    street_address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    location_type: 'on-site',
    experience_level: 'mid',
    due_date: ''
  });

  const [responsibilities, setResponsibilities] = useState(['']);
  const [who_you_are, setWhoYouAre] = useState(['']);
  const [niceToHave, setNiceToHave] = useState(['']);
  const [imageUrls, setImageUrls] = useState(['']);

  // Step configuration
  const steps = [
    { number: 1, title: 'Job Title', icon: Briefcase },
    { number: 2, title: 'Job Description', icon: FileText },
    { number: 3, title: 'Employment Type', icon: Users },
    { number: 4, title: 'Experience & Location', icon: MapPin },
    { number: 5, title: 'Responsibilities', icon: Award },
    { number: 6, title: 'Who You Are', icon: Users },
    { number: 7, title: 'Nice to Haves', icon: Gift },
    { number: 8, title: 'Job Category', icon: Briefcase },
    { number: 9, title: 'Skills', icon: Award },
    { number: 10, title: 'Salary', icon: DollarSign },
    { number: 11, title: 'Location Details', icon: MapPin },
    { number: 12, title: 'Benefits', icon: Gift },
    { number: 13, title: 'Job Images', icon: Camera },
    { number: 14, title: 'Deadline', icon: Calendar },
    { number: 15, title: 'Review', icon: Anchor }
  ];

  const totalSteps = steps.length;

  // City search effect
  useEffect(() => {
    const searchCity = async () => {
      if (citySearch.length < 2) {
        setCities([]);
        return;
      }
      
      setCitySearchLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/skills/api/city/search?search_term=${encodeURIComponent(citySearch)}&limit=10`);
        
        if (response.ok) {
          const data = await response.json();
          setCities(data);
        } else {
          console.error('Error fetching city suggestions:', response.statusText);
          setCities([]);
        }
      } catch (err) {
        console.error('Error fetching city suggestions:', err);
      } finally {
        setCitySearchLoading(false);
      }
    };
    
    const timeoutId = setTimeout(searchCity, 300);
    return () => clearTimeout(timeoutId);
  }, [citySearch]);

  // Fetch skills and categories
  const fetchAllSkillsFromBackend = async () => {
    setIsLoadingSkills(true);
    
    const token = localStorage.getItem('token');
    
    if (!token) {
      setIsLoadingSkills(false);
      return [];
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/skills/all-skills`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        const groupedSkills = data.reduce((acc, skill) => {
          const categoryName = skill.category;
          if (!acc[categoryName]) {
            acc[categoryName] = [];
          }
          acc[categoryName].push(skill);
          return acc;
        }, {});
        
        setSkillsCategories(groupedSkills);
        
        const defaultExpanded = {};
        Object.keys(groupedSkills).slice(0, 3).forEach(category => {
          defaultExpanded[category] = true;
        });
        setExpandedCategories(defaultExpanded);
        
      } else {
        console.error('Error fetching skills:', response.statusText);
      }
    } catch (err) {
      console.error('Error fetching skills:', err);
    } finally {
      setIsLoadingSkills(false);
    }
  };

  const fetchJobCategoriesFromBackend = async () => {
    setIsLoadingCategories(true);
    
    const token = localStorage.getItem('token');
    
    if (!token) {
      setIsLoadingCategories(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/jobs/v2/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        const groupedCategories = data.reduce((acc, category) => {
          const categoryName = category.category_name;
          if (!acc[categoryName]) {
            acc[categoryName] = [];
          }
          acc[categoryName].push(category);
          return acc;
        }, {});
        
        setJobCategories(groupedCategories);
        
        const defaultExpanded = {};
        Object.keys(groupedCategories).slice(0, 3).forEach(category => {
          defaultExpanded[category] = true;
        });
        setExpandedJobCategories(defaultExpanded);
        
      } else {
        console.error('Error fetching job categories:', response.statusText);
      }
    } catch (err) {
      console.error('Error fetching job categories:', err);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchAllSkillsFromBackend();
    fetchJobCategoriesFromBackend();
  }, []);

  // Helper functions
  const addResponsibility = () => {
    setResponsibilities([...responsibilities, '']);
  };
  
  const removeResponsibility = (index) => {
    const updated = responsibilities.filter((_, i) => i !== index);
    setResponsibilities(updated.length === 0 ? [''] : updated);
  };
  
  const updateResponsibility = (index, value) => {
    const updated = [...responsibilities];
    updated[index] = value;
    setResponsibilities(updated);
  };

  const addWhoYouAre = () => {
    setWhoYouAre([...who_you_are, '']);
  };
  
  const removeAddWhoYouAre = (index) => {
    const updated = who_you_are.filter((_, i) => i !== index);
    setWhoYouAre(updated.length === 0 ? [''] : updated);
  };
  
  const updateAddWhoYouAre = (index, value) => {
    const updated = [...who_you_are];
    updated[index] = value;
    setWhoYouAre(updated);
  };

  const addNicetohave = () => {
    setNiceToHave([...niceToHave, '']);
  };
  
  const removeNicetohave = (index) => {
    const updated = niceToHave.filter((_, i) => i !== index);
    setNiceToHave(updated.length === 0 ? [''] : updated);
  };
  
  const updateNicetohave = (index, value) => {
    const updated = [...niceToHave];
    updated[index] = value;
    setNiceToHave(updated);
  };

  const getCurrencySymbol = (currencyCode) => {
    const currencySymbols = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CNY': '¥', 'KRW': '₩', 'INR': '₹',
      'CAD': 'C$', 'AUD': 'A$', 'CHF': 'Fr', 'SEK': 'kr', 'NOK': 'kr', 'DKK': 'kr',
      'PLN': 'zł', 'CZK': 'Kč', 'HUF': 'Ft', 'RUB': '₽', 'BRL': 'R$', 'MXN': '$',
      'ZAR': 'R', 'SGD': 'S$', 'HKD': 'HK$', 'NZD': 'NZ$', 'THB': '฿', 'MYR': 'RM',
      'PHP': '₱', 'IDR': 'Rp', 'VND': '₫', 'TRY': '₺', 'ILS': '₪', 'AED': 'د.إ',
      'SAR': '﷼', 'EGP': '£', 'NGN': '₦'
    };
    return currencySymbols[currencyCode?.toUpperCase()] || currencyCode || '$';
  };

  // Event handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSkillToggle = (skill) => {
    const isSelected = formData.skills.find(s => s.skill_id === skill.skill_id);
    
    if (isSelected) {
      setFormData(prev => ({
        ...prev,
        skills: prev.skills.filter(s => s.skill_id !== skill.skill_id)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, skill]
      }));
    }
  };

  const handleCategorySelect = (category) => {
    setFormData(prev => ({
      ...prev,
      category: category
    }));
  };

  const toggleCategoryExpansion = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const toggleJobCategoryExpansion = (categoryName) => {
    setExpandedJobCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const handleBenefitAdd = () => {
    const newBenefit = {
      id: Date.now(),
      benefit_name: '',
      benefit_description: ''
    };
    setFormData(prev => ({
      ...prev,
      benefits: [...prev.benefits, newBenefit]
    }));
  };

  const handleBenefitUpdate = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.map(benefit =>
        benefit.id === id ? { ...benefit, [field]: value } : benefit
      )
    }));
  };

  const handleBenefitRemove = (id) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter(benefit => benefit.id !== id)
    }));
  };

  const handleCitySelect = (city) => {
    setFormData(prev => ({
      ...prev,
      city: city.city_id,
      city_name: city.city_name,
      state: city.state,
      country: city.country_id,
      country_name: city.name,
    }));
    setCitySearch(city);
    setShowCityDropdown(false);
  };

  const nextStep = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    const requirements = [];
    let order = 1;

    if (responsibilities?.length) {
      requirements.push(...responsibilities.map(item => ({
        requirement_type: 'experience',
        description: item,
        order_number: order++
      })));
    }

    if (who_you_are?.length) {
      requirements.push(...who_you_are.map(item => ({
        requirement_type: 'essential',
        description: item,
        order_number: order++
      })));
    }

    if (niceToHave?.length) {
      requirements.push(...niceToHave.map(item => ({
        requirement_type: 'preferred',
        description: item,
        order_number: order++
      })));
    }

    const jobData = {
      company_id: selectedCompany.company_id,
      title: formData.title,
      description: formData.description,
      job_type: formData.job_type,
      location_type: formData.location_type,
      experience_level: formData.experience_level,
      currency_code: formData.currency_code,
      salary_higher_rate: formData.salary_higher_rate,
      salary_lower_rate: formData.salary_lower_rate,
      category: formData.category?.id || '',
      skills: formData.skills.map(skill => skill.skill_id),
      street_address: formData.street_address,
      city: formData.city,
      state: "",
      country: formData.country,
      postal_code: formData.postal_code,
      due_date: formData.due_date,
      requirements: requirements,
      benefits: formData.benefits,
      images: imageUrls.map(img => ({
        image_url: img,
        image_description: "",
        is_primary: false
      }))
    };

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        return;
      }
      setHideForm(true);
      setSubmitting(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/jobs/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(jobData)
      });

      if (response.ok) {
        const result = await response.json();
        setSubmissionResult({
          'success': true,
          'jobId': result.id,
          'error': false,
          'message': '',
          'errors': []
        });
      } else {
        const errorData = await response.json();
        setSubmissionResult({
          'success': false,
          'jobId': '',
          'error': true,
          'message': errorData.details || 'Failed to create job',
          'errors': []
        });
      } 
    } catch (error) {
      console.error('Error creating job:', error);
      setSubmissionResult({
        'success': false,
        'jobId': '',
        'error': true,
        'message': 'Failed to create job',
        'errors': []
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getProgressPercentage = () => {
    return (currentStep / totalSteps) * 100;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Job Title
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Job Title</h2>
              <p>Enter a clear and descriptive job title</p>
            </div>
            <div className="form-group">
              <label>Job Title</label>
              <p className="field-description">Job titles must describe one position</p>
              <input
                type="text"
                placeholder="e.g. Software Engineer"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
              />
              <div className="char-count">At least 10 characters</div>
            </div>
          </div>
        );

      case 2: // Job Description
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Job Description</h2>
              <p>Provide a detailed description of the role</p>
            </div>
            <div className="form-group">
              <label>Job Description</label>
              <p className="field-description">Describe the main purpose and overview of this role</p>
              <textarea
                placeholder="Enter job description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={6}
              />
              <div className="char-count">Maximum 500 characters - {formData.description.length} / 500</div>
            </div>
          </div>
        );

      case 3: // Employment Type
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Type of Employment</h2>
              <p>Select the employment type for this position</p>
            </div>
            <div className="form-group">
              <label>Employment Type</label>
              <select
                value={formData.job_type || ''}
                onChange={(e) => handleInputChange('job_type', e.target.value)}
              >
                <option value="">Select Employment Type</option>
                <option value="full-time">Full-Time</option>
                <option value="part-time">Part-Time</option>
                <option value="contract">Contract</option>
                <option value="internship">Internship</option>
                <option value="freelance">Freelance</option>
              </select>
            </div>
          </div>
        );

      case 4: // Experience Level & Location Type
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Experience & Location</h2>
              <p>Set the experience level and work arrangement</p>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Experience Level</label>
                <select
                  value={formData.experience_level}
                  onChange={(e) => handleInputChange('experience_level', e.target.value)}
                >
                  <option value="junior">Junior</option>
                  <option value="mid">Mid-Level</option>
                  <option value="senior">Senior</option>
                  <option value="lead">Lead</option>
                </select>
              </div>
              <div className="form-group">
                <label>Location Type</label>
                <select
                  value={formData.location_type}
                  onChange={(e) => handleInputChange('location_type', e.target.value)}
                >
                  <option value="on-site">On-site</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 5: // Responsibilities
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Responsibilities</h2>
              <p>Outline the core responsibilities of the position</p>
            </div>
            <RequirementList
              buttonLabel={"+ Add Responsibility"}
              responsibilities={responsibilities}
              label={"Responsibilities"}
              description={"Outline the core responsibilities of the position"}
              updateResponsibility={updateResponsibility}
              removeResponsibility={removeResponsibility}
              addResponsibility={addResponsibility}
            />
          </div>
        );

      case 6: // Who You Are
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Who You Are</h2>
              <p>Add your preferred candidate qualifications</p>
            </div>
            <RequirementList
              buttonLabel={"+ Add Qualification"}
              responsibilities={who_you_are}
              label={"Who You Are"}
              description={"Add your preferred candidates qualifications"}
              updateResponsibility={updateAddWhoYouAre}
              removeResponsibility={removeAddWhoYouAre}
              addResponsibility={addWhoYouAre}
            />
          </div>
        );

      case 7: // Nice to Haves
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Nice To Haves</h2>
              <p>Add nice-to-have skills and qualifications</p>
            </div>
            <RequirementList
              buttonLabel={"+ Add Nice to have"}
              responsibilities={niceToHave}
              label={"Nice To Haves"}
              description={"Add nice-to-have skills and qualifications for the role"}
              updateResponsibility={updateNicetohave}
              removeResponsibility={removeNicetohave}
              addResponsibility={addNicetohave}
            />
          </div>
        );

      case 8: // Job Category
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Job Category</h2>
              <p>Select the job category that best fits this position</p>
            </div>
            <div className="form-group">
              {formData.category && (
                <div className="selected-skills-summary">
                  <h4>Selected Category</h4>
                  <div className="selected-skills">
                    <span className="skill-tag selected">
                      {formData.category.name}
                      <button type="button" onClick={() => handleCategorySelect('')}>
                        <X size={14} />
                      </button>
                    </span>
                  </div>
                </div>
              )}

              <div className="skills-categories-container">
                {isLoadingCategories ? (
                  <div className="skills-loading">
                    <div className="loading-spinner"></div>
                    Loading categories...
                  </div>
                ) : (
                  Object.entries(jobCategories).map(([categoryName, categories]) => (
                    <div key={categoryName} className="skill-category">
                      <div 
                        className="category-header"
                        onClick={() => toggleJobCategoryExpansion(categoryName)}
                      >
                        <span className="category-name">{categoryName}</span>
                        <span className="category-count">({categories.length})</span>
                        {expandedJobCategories[categoryName] ? 
                          <ChevronUp size={16} /> : 
                          <ChevronDown size={16} />
                        }
                      </div>
                      {expandedJobCategories[categoryName] && (
                        <div className="category-skills">
                          {categories.map(category => {
                            const isSelected = formData.category?.id === category.id;
                            return (
                              <button
                                key={category.id}
                                type="button"
                                className={`skill-button ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleCategorySelect(category)}
                              >
                                {category.name}
                                {isSelected && <X size={14} className="remove-icon" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );

      case 9: // Skills
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Required Skills</h2>
              <p>Select skills required for this job</p>
            </div>
            <div className="form-group">
              {formData.skills.length > 0 && (
                <div className="selected-skills-summary">
                  <h4>Selected Skills ({formData.skills.length})</h4>
                  <div className="selected-skills">
                    {formData.skills.map(skill => (
                      <span key={skill.skill_id} className="skill-tag selected">
                        {skill.name}
                        <button type="button" onClick={() => handleSkillToggle(skill)}>
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="skills-categories-container">
                {isLoadingSkills ? (
                  <div className="skills-loading">
                    <div className="loading-spinner"></div>
                    Loading skills...
                  </div>
                ) : (
                  Object.entries(skillsCategories).map(([categoryName, skills]) => (
                    <div key={categoryName} className="skill-category">
                      <div 
                        className="category-header"
                        onClick={() => toggleCategoryExpansion(categoryName)}
                      >
                        <span className="category-name">{categoryName}</span>
                        <span className="category-count">({skills.length})</span>
                        {expandedCategories[categoryName] ? 
                          <ChevronUp size={16} /> : 
                          <ChevronDown size={16} />
                        }
                      </div>
                      {expandedCategories[categoryName] && (
                        <div className="category-skills">
                          {skills.map(skill => {
                            const isSelected = formData.skills.find(s => s.skill_id === skill.skill_id);
                            return (
                              <button
                                key={skill.skill_id}
                                type="button"
                                className={`skill-button ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleSkillToggle(skill)}
                              >
                                {skill.name}
                                {isSelected && <X size={14} className="remove-icon" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );

      case 10: // Salary
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Salary Information</h2>
              <p>Specify the salary range for this position</p>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Currency</label>
                <select
                  value={formData.currency_code}
                  onChange={(e) => handleInputChange('currency_code', e.target.value)}
                >
                  <option value="USD">USD ($) - US Dollar</option>
                  <option value="EUR">EUR (€) - Euro</option>
                  <option value="GBP">GBP (£) - British Pound</option>
                  <option value="CAD">CAD (C$) - Canadian Dollar</option>
                  <option value="AUD">AUD (A$) - Australian Dollar</option>
                  <option value="JPY">JPY (¥) - Japanese Yen</option>
                  <option value="CHF">CHF (Fr) - Swiss Franc</option>
                  <option value="CNY">CNY (¥) - Chinese Yuan</option>
                  <option value="INR">INR (₹) - Indian Rupee</option>
                  <option value="SGD">SGD (S$) - Singapore Dollar</option>
                  <option value="HKD">HKD (HK$) - Hong Kong Dollar</option>
                  <option value="NZD">NZD (NZ$) - New Zealand Dollar</option>
                </select>
              </div>
              <div className="form-group">
                <label>Salary Range</label>
                <div className="salary-range">
                  <div className="salary-input">
                    <span className="currency">{getCurrencySymbol(formData.currency_code)}</span>
                    <input
                      type="number"
                      placeholder="5,000"
                      value={formData.salary_lower_rate}
                      onChange={(e) => handleInputChange('salary_lower_rate', e.target.value)}
                    />
                  </div>
                  <span className="to">to</span>
                  <div className="salary-input">
                    <span className="currency">{getCurrencySymbol(formData.currency_code)}</span>
                    <input
                      type="number"
                      placeholder="22,000"
                      value={formData.salary_higher_rate}
                      onChange={(e) => handleInputChange('salary_higher_rate', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 11: // Location Details
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Location Details</h2>
              <p>Specify the job location information</p>
            </div>
            <div className="form-group">
              <label>Street Address</label>
              <input
                type="text"
                placeholder="Enter street address"
                value={formData.street_address}
                onChange={(e) => handleInputChange('street_address', e.target.value)}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <div className="city-search-container">
                  <input
                    type="text"
                    placeholder="Search city..."
                    value={citySearch.city_name}
                    onChange={(e) => {
                      setCitySearch(e.target.value);
                      setShowCityDropdown(true);
                    }}
                    onFocus={() => setShowCityDropdown(true)}
                  />
                  {showCityDropdown && (
                    <div className="city-dropdown">
                      {cities.map(city => (
                        <div
                          key={city.city_id}
                          className="city-option"
                          onClick={() => handleCitySelect(city)}
                        >
                          {city.city_name}, {city.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Postal Code</label>
                <input
                  type="text"
                  placeholder="Enter postal code"
                  value={formData.postal_code}
                  onChange={(e) => handleInputChange('postal_code', e.target.value)}
                />
              </div>
            </div>
          </div>
        );

      case 12: // Benefits
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Perks and Benefits</h2>
              <p>List out your top perks and benefits</p>
            </div>
            <div className="form-group">
              <div className="benefits-header">
                <label>Perks and Benefits</label>
                <button type="button" className="add-benefit-btn" onClick={handleBenefitAdd}>
                  <Plus size={16} />
                  Add Benefit
                </button>
              </div>
              <p className="field-description">Encourage more people to apply by sharing attractive rewards and benefits</p>
              
              <div className="benefits-grid">
                {formData.benefits.map(benefit => (
                  <div key={benefit.id} className="benefit-card">
                    <button 
                      type="button" 
                      className="remove-benefit"
                      onClick={() => handleBenefitRemove(benefit.id)}
                    >
                      <X size={16} />
                    </button>
                    <div className="benefit-icon">💼</div>
                    <input
                      type="text"
                      placeholder="Benefit Name"
                      value={benefit.benefit_name}
                      onChange={(e) => handleBenefitUpdate(benefit.id, 'benefit_name', e.target.value)}
                      className="benefit-name"
                    />
                    <textarea
                      placeholder="Benefit description"
                      value={benefit.benefit_description}
                      onChange={(e) => handleBenefitUpdate(benefit.id, 'benefit_description', e.target.value)}
                      className="benefit-description"
                      rows={3}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 13: // Job Images
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Job Images</h2>
              <p>Upload images to showcase your workplace and team</p>
            </div>
            <div className="form-group">
              <ImageUrlUploader imageUrls={imageUrls} setImageUrls={setImageUrls} />
            </div>
          </div>
        );

      case 14: // Application Deadline
        return (
          <div className="step-content">
            <div className="section-header">
              <h2>Application Deadline</h2>
              <p>Set when applications for this job should close</p>
            </div>
            <div className="form-group">
              <label>Application Deadline</label>
              <p className="field-description">Select the last date candidates can apply for this position</p>
              <input
                type="date"
                min={new Date().toISOString().split('T')[0]} 
                value={formData.due_date}
                onChange={(e) => handleInputChange('due_date', e.target.value)}
              />
            </div>
          </div>
        );

      case 15: // Review
        return (
          <JobReview 
            jobData={formData}
            onEdit={(step) => {setCurrentStep(step)}}
            onSubmit={handleSubmit}
            onBack={prevStep}
            responsibilities={responsibilities}
            who_you_are={who_you_are}
            niceToHave={niceToHave}
            imageUrls={imageUrls}
          />
        );

      default:
        return null;
    }
  };

  const StepIndicator = () => (
    <div className="step-progress">
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${getProgressPercentage()}%` }}
        ></div>
      </div>
      <div className="step-info">
        <span className="step-number">Step {currentStep} of {totalSteps}</span>
        <span className="step-title">{steps[currentStep - 1]?.title}</span>
      </div>
    </div>
  );

  return (
    <Dashboard page={"home"}>
      <div className="job-posting-container">
        <div className="create-header">
          <h1>Post a Job</h1>
        </div>

        <StepIndicator />

        {hideForm ? (
          <FormSubmissionStatus 
            submissionResult={submissionResult}
            isSubmitting={isSubmitting}
            jobTitle={formData.title}
            onClose={() => {navigate("/listing")}}
            onRetry={() => {
              setCurrentStep(1);
              setHideForm(false);
              setSubmitting(false);
            }}
          />
        ) : (
          <div className="form-container">
            {renderStepContent()}

            <div className="form-actions">
              {currentStep > 1 && (
                <button type="button" className="btn-secondary" onClick={prevStep}>
                  Previous
                </button>
              )}
              {currentStep < totalSteps ? (
                <button type="button" className="btn-primary" onClick={nextStep}>
                  Next Step
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </Dashboard>
  );
};

export default JobPostingForm;